import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    CopyOutlined,
    CreditCardOutlined,
    LinkOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { Plan } from '../store/user/types';

const { Text } = Typography;

interface OrganizationPaymentRequest {
    id: string;
    organizationId: string;
    organizationName: string;
    organizationEmail?: string | null;
    organizationPhone?: string | null;
    clientName?: string | null;
    clientRuc?: string | null;
    clientEmail?: string | null;
    clientPhone?: string | null;
    currentContractId?: string | null;
    planId: string;
    planName: string;
    source?: string | null;
    status: string;
    invoiceStatus: string;
    currency: string;
    total: number;
    returnUrl?: string | null;
    expiresAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
}

interface PayPhonePrepareResponse {
    clientTransactionId: string;
    paymentId?: string | null;
    payWithPayPhone?: string | null;
    payWithCard?: string | null;
    bridgeToken?: string | null;
}

interface Props {
    plans: Plan[];
    getPendingCardValidationRequests: (organizationId?: string, search?: string) => Promise<OrganizationPaymentRequest[]>;
    regeneratePaymentRequestPayPhone: (paymentRequestId: string, value: Object) => Promise<PayPhonePrepareResponse | null>;
}

const money = (value?: number, currency = 'USD') => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('es-EC', { style: 'currency', currency }).format(amount);
};

const formatDate = (value?: string | null) => {
    if (!value) {
        return 'No registrado';
    }
    return new Date(value).toLocaleDateString('es-EC');
};

const hasPlanTrial = (plan?: Plan | null) => Number(plan?.trialDays || 0) > 0;

const getPlanInitialAmount = (plan?: Plan | null) => {
    if (!plan) {
        return 0;
    }
    return hasPlanTrial(plan) ? Number(plan.trialValidationAmount || 0) : Number(plan.price || 0);
};

const PlanBillingPendingCardValidation: React.FC<Props> = ({
    plans,
    getPendingCardValidationRequests,
    regeneratePaymentRequestPayPhone
}) => {
    const [form] = Form.useForm();
    const selectedPlanId = Form.useWatch('planId', form);
    const [rows, setRows] = useState<OrganizationPaymentRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedRequest, setSelectedRequest] = useState<OrganizationPaymentRequest | null>(null);
    const [generatedLink, setGeneratedLink] = useState('');

    const paidPlans = useMemo(() => {
        return plans.filter(plan =>
            Number(plan.price || 0) > 0
            && String(plan.type || '').toUpperCase() !== 'FREE'
            && String(plan.status || '').toUpperCase() === 'ACTIVO'
        );
    }, [plans]);

    const selectedPlan = paidPlans.find(plan => plan.id === selectedPlanId) || null;

    const loadRows = useCallback(async (nextSearch?: string) => {
        setLoading(true);
        const criteria = nextSearch !== undefined ? nextSearch : search;
        const response = await getPendingCardValidationRequests(undefined, criteria || undefined);
        setRows(response || []);
        setLoading(false);
    }, [getPendingCardValidationRequests, search]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const openRegenerateModal = (record: OrganizationPaymentRequest) => {
        const defaultPlan = paidPlans.find(plan => plan.id === record.planId) || paidPlans[0];
        setSelectedRequest(record);
        setGeneratedLink(record.returnUrl || '');
        form.setFieldsValue({ planId: defaultPlan?.id });
    };

    const handleRefresh = () => {
        setSearch('');
        loadRows('');
    };

    const copyLink = async (url: string) => {
        if (!url) {
            return;
        }
        await navigator.clipboard?.writeText(url);
        message.success('Link copiado');
    };

    const regenerate = async () => {
        if (!selectedRequest) {
            return;
        }

        const values = await form.validateFields();
        const plan = paidPlans.find(item => item.id === values.planId);
        const response = await regeneratePaymentRequestPayPhone(selectedRequest.id, {
            planId: values.planId,
            currentContractId: selectedRequest.currentContractId || null,
            source: 'ADMIN',
            reference: `Validacion tarjeta ${selectedRequest.organizationName} - ${plan?.name || selectedRequest.planName}`
        });

        const url = response?.payWithCard || response?.payWithPayPhone || '';
        if (!url) {
            Modal.warning({
                title: 'PayPhone',
                content: 'No se pudo generar el link puente de pago.'
            });
            return;
        }

        setGeneratedLink(url);
        await loadRows();
    };

    return (
        <Space direction="vertical" size={18} style={{ width: '100%' }}>
            <div className="plan-billing-panel">
                <div className="plan-billing-filter-grid">
                    <div>
                        <Text strong>Buscar intento</Text>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Organizacion, cliente, RUC, email o plan"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                            onPressEnter={() => loadRows()}
                        />
                    </div>
                    <Button icon={<SearchOutlined />} type="primary" onClick={() => loadRows()}>
                        Buscar
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                        Actualizar
                    </Button>
                </div>
            </div>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={rows}
                pagination={{ pageSize: 8 }}
                scroll={{ x: 1120 }}
                columns={[
                    {
                        title: 'Organizacion',
                        dataIndex: 'organizationName',
                        render: (_value, record: OrganizationPaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Text strong>{record.organizationName}</Text>
                                <Text type="secondary">{record.organizationEmail || 'Sin email'}</Text>
                                <Text type="secondary">Tel: {record.organizationPhone || 'No registrado'}</Text>
                            </Space>
                        )
                    },
                    {
                        title: 'Datos de facturacion',
                        dataIndex: 'clientName',
                        render: (_value, record: OrganizationPaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Text strong>{record.clientName || 'Sin cliente'}</Text>
                                <Text type="secondary">{record.clientRuc || 'Sin RUC'}</Text>
                                <Text type="secondary">{record.clientEmail || 'Sin email'}</Text>
                            </Space>
                        )
                    },
                    {
                        title: 'Plan solicitado',
                        dataIndex: 'planName',
                        render: (_value, record: OrganizationPaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Text>{record.planName}</Text>
                                <Text type="secondary">{money(record.total, record.currency)} inicial</Text>
                            </Space>
                        )
                    },
                    {
                        title: 'Estado',
                        dataIndex: 'status',
                        render: (status: string, record: OrganizationPaymentRequest) => (
                            <Space direction="vertical" size={0}>
                                <Tag color={status === 'ERROR' ? 'red' : 'orange'}>{status}</Tag>
                                <Text type="secondary">Vence: {formatDate(record.expiresAt)}</Text>
                            </Space>
                        )
                    },
                    {
                        title: '',
                        key: 'actions',
                        align: 'right' as const,
                        render: (_value, record: OrganizationPaymentRequest) => (
                            <Button icon={<CreditCardOutlined />} onClick={() => openRegenerateModal(record)}>
                                Regenerar link
                            </Button>
                        )
                    }
                ]}
            />

            <Modal
                open={!!selectedRequest}
                title="Regenerar link PayPhone"
                onCancel={() => {
                    setSelectedRequest(null);
                    setGeneratedLink('');
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setSelectedRequest(null);
                        setGeneratedLink('');
                    }}>
                        Cerrar
                    </Button>,
                    <Button key="generate" type="primary" icon={<LinkOutlined />} onClick={regenerate}>
                        Generar link
                    </Button>
                ]}
                width={760}
                destroyOnHidden
            >
                {selectedRequest && (
                    <Space direction="vertical" style={{ width: '100%' }} size={16}>
                        <Alert
                            type="info"
                            showIcon
                            message="Puede conservar el plan solicitado o cambiarlo por otro plan pagado antes de enviar el nuevo link puente."
                        />
                        <Form form={form} layout="vertical">
                            <Form.Item
                                name="planId"
                                label="Plan para este intento"
                                rules={[{ required: true, message: 'Seleccione el plan' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    options={paidPlans.map(plan => ({
                                        value: plan.id || '',
                                        label: `${plan.name} - ${money(getPlanInitialAmount(plan), 'USD')}${hasPlanTrial(plan) ? ' validacion' : ''}`
                                    }))}
                                />
                            </Form.Item>
                        </Form>
                        {selectedPlan && (
                            <Alert
                                type="success"
                                showIcon
                                message={hasPlanTrial(selectedPlan)
                                    ? `Se cobrara ${money(getPlanInitialAmount(selectedPlan), 'USD')} para validar la tarjeta y luego se reversara.`
                                    : `Se cobrara ${money(getPlanInitialAmount(selectedPlan), 'USD')} como pago inicial del plan.`}
                            />
                        )}
                        {generatedLink && (
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text strong>Link puente</Text>
                                <Input.TextArea value={generatedLink} autoSize={{ minRows: 2, maxRows: 4 }} readOnly />
                                <Space wrap>
                                    <Button icon={<CopyOutlined />} onClick={() => copyLink(generatedLink)}>
                                        Copiar
                                    </Button>
                                    <Button icon={<LinkOutlined />} onClick={() => window.open(generatedLink, '_blank')}>
                                        Abrir puente
                                    </Button>
                                </Space>
                            </Space>
                        )}
                    </Space>
                )}
            </Modal>
        </Space>
    );
};

export default PlanBillingPendingCardValidation;
